import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import _, { debounce } from "lodash";
import { useOutletContext } from "react-router-dom";
import { Badge, Box } from "@mui/material";
import {
  Button,
  SIZE,
  COLOR,
  VARIANT,
} from "@metacrm/metacrm-material-ui/dist/Button";
import { apiUrl } from "features/configure";
import axios from "axios";
import { useTheme } from "@mui/styles";
import ImagePreview from "features/metadesk/components/ImagePreview";
import ImageUpload from "features/metadesk/components/ImageUpload";
import { InputField } from "@metacrm/metacrm-material-ui/dist/InputField";
import { Loading } from "features/common";
import { useConnectWallet } from "features/home/redux/hooks";
import { useFetchEntityConfig } from "features/metadesk/redux/hooks";
import { useIntl } from "react-intl";
import useGetPagePermission from "hooks/useGetPagePermission";
import { uploadFiles } from "features/helpers/utils";
import { TinyText } from "features/metadesk/settings/integrations/widget/components/WidgetLauncher.styles";
import AutoCompleteDropdown from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useForm } from "react-hook-form";
import IssueDetailCard from "./IssueDetailCard";
import IssueIntegrationView from "./IssueIntegration";
import {
  StyledBackBtn,
  StyledBottomBar,
  StyledContainer,
  StyledContent,
  StyledIssueDropdown,
  StyledIssueDropdownItem,
  StyledIssueSignature,
  StyledList,
  StyledNoResultFound,
  StyledUploadAllImgText,
  StyledUploadBoxArea,
} from "./Issue.styles";

const JiraIssue = ({ selectedTicket }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const dispatchSnackbar = useDispatchSnackbar();
  const { userData } = useConnectWallet();
  const { entityConfig } = useFetchEntityConfig();
  const [entityName] = useOutletContext();
  const pagePermission = useGetPagePermission();
  const { canEdit: canEditTicket } = pagePermission("tickets");
  const { canEdit: canEditIntegration } = pagePermission("integration");

  const [openCreate, setOpenCreate] = useState(false);
  const [openAttachOldIssue, setOpenAttachOldIssue] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isNewIssueAdded, setIsNewIssueAdded] = useState(false);

  const [formData, setFormData] = useState([]);
  const [project, setProject] = useState(null);
  const [issueType, setIssueType] = useState(null);
  const [assignee, setAssignee] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imgs, setImgs] = useState([]);

  const [searchIssueText, setSearchIssueText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResult, setShowSearchResult] = useState(false);
  const [isSearchResultLoading, setIsSearchResultLoading] = useState(false);

  const [issues, setIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState({});

  const dropdownRef = useRef(null);

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors: errorsInfo, isValid },
    clearErrors,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {},
  });

  let initDescription = `------------------------- signature -------------------------\n
  Ticket url: ${_.get(window, "location.href")}\n\nReporter: ${_.get(
    userData,
    "team.name",
    ""
  )}\n`;

  const isJiraConnected = _.get(entityConfig, "serviceModule.jira.connected");

  useEffect(async () => {
    if (selectedTicket && _.get(entityConfig, "serviceModule.jira.connected")) {
      await fetchJiraIssues();
    }
  }, [selectedTicket, entityConfig]);

  useEffect(() => {
    if (openCreate && _.isEmpty(formData)) {
      fetchCreateIssueFormData();
    }
  }, [openCreate]);

  useEffect(async () => {
    if (isNewIssueAdded) {
      await fetchJiraIssues();
    }
  }, [isNewIssueAdded]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('input[type="search"]')) {
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSearchResult(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addImages = (images) => {
    const uniqueImages = _.uniq([...imgs, ...images]);
    setImgs(uniqueImages);
  };

  const issueTypeOptions = useMemo(() => {
    if (project) {
      return _.get(
        _.find(formData.projectIssues, { id: project.value }),
        "issueTypes",
        []
      )
        .filter((i) => i.name !== "Subtask")
        .map((l) => {
          return {
            ...l,
            label: l.name,
            value: l.id,
          };
        });
    }
  }, [project]);

  const assigneeOptions = useMemo(() => {
    if (!_.isEmpty(formData)) {
      return _.get(formData, "assignees", []).map((m) => {
        return { ...m, label: m.name, value: m.id };
      });
    }
  }, [formData]);

  const clearFormFields = () => {
    setProject(null);
    setIssueType(null);
    setAssignee(null);
    setTitle("");
    setDescription("");
    setImgs([]);
    reset();
  };

  const fetchJiraIssues = async () => {
    setIsLoading(true);
    try {
      setIsNewIssueAdded(false);
      let { data: issueList } = await axios.get(
        apiUrl + `/api/jira/${entityName}/issues/${selectedTicket._id}`
      );
      setIssues(issueList);
    } catch (err) {
      console.log(err);
      dispatchSnackbar({
        message: formatMessage({ id: "global.snackbar.error.fetchFormData" }),
        variant: "error",
      });
    }
    setIsLoading(false);
  };

  const fetchCreateIssueFormData = async () => {
    setIsLoading(true);
    try {
      let response = await axios.get(
        apiUrl + `/api/jira/${entityName}/getCreateIssueFormData`
      );
      setFormData(response.data);
    } catch (err) {
      console.log(err);
      dispatchSnackbar({
        message: formatMessage({ id: "global.snackbar.error.fetchFormData" }),
        variant: "error",
      });
    }
    setIsLoading(false);
  };

  const handleCreateIssue = async () => {
    setIsLoading(true);
    try {
      const images = await uploadFiles(imgs, entityName);
      const payload = {
        projectId: project.value,
        title,
        description,
        assigneeId: assignee?.id || "",
        issueTypeId: issueType.id,
        images,
        ticketUrl: _.get(window, "location.href"),
        reporter: _.get(userData, "team.name", ""),
      };
      await axios.post(
        apiUrl + `/api/jira/${entityName}/issue/${selectedTicket._id}`,
        payload
      );
      setIsNewIssueAdded(true);
      setOpenCreate(false);
      clearFormFields();
    } catch (err) {
      console.log(err);
      dispatchSnackbar({
        message: formatMessage({ id: "global.snackbar.error.createIssue" }),
        variant: "error",
      });
    }
    setIsLoading(false);
  };

  const getAllRelatedFiles = async () => {
    try {
      let messages = await axios.get(
        `${apiUrl}/api/messages/ticket?id=${selectedTicket._id}&userId=${_.get(
          selectedTicket,
          "user._id"
        )}&entityName=${entityName}`
      );
      let images = [];
      for (let message of messages.data) {
        if (message.imagesUrl && message.imagesUrl.length > 0) {
          // Filter the URLs to include only jpg, png, and pdf extensions
          let filteredImages = message.imagesUrl.filter((url) => {
            const extension = url.split(".").pop().toLowerCase();
            return ["jpg", "png", "pdf"].includes(extension);
          });
          images = [...images, ...filteredImages];
        }
      }
      addImages(images);
    } catch (err) {
      console.log(err);
      dispatchSnackbar({
        message: formatMessage({ id: "global.snackbar.error.noImagesFound" }),
        variant: "error",
      });
    }
  };

  const handleSearchIssue = async (searchKey) => {
    setIsSearchResultLoading(true);
    try {
      setShowSearchResult(false);
      let { data: searchResult } = await axios.get(
        apiUrl + `/api/jira/${entityName}/search/issues?searchText=${searchKey}`
      );
      setSearchResults(searchResult);
      setShowSearchResult(true);
    } catch (err) {
      console.log(err);
      dispatchSnackbar({
        message: formatMessage({ id: "global.snackbar.error.commom" }),
        variant: "error",
      });
    }
    setIsSearchResultLoading(false);
  };

  const debouncedSearch = useCallback(
    debounce((searchKey) => {
      handleSearchIssue(searchKey.trim());
    }, 500),
    []
  );

  const handleSearch = (e) => {
    let searchKey = e.target.value;
    setSearchIssueText(searchKey);
    if (searchKey) {
      debouncedSearch(searchKey);
    }
  };

  const detachIssue = async (issueId) => {
    setIsLoading(true);
    try {
      await axios.post(
        apiUrl + `/api/jira/${entityName}/issue/detach/${selectedTicket._id}`,
        { issueId }
      );
      let updatedIssueList = _.filter(
        issues,
        (issue) => issue.issueId != issueId
      );
      setIssues(updatedIssueList);
    } catch (err) {
      console.log(err);
      dispatchSnackbar({
        message: formatMessage({ id: "Error detaching issue" }),
        variant: "error",
      });
    }
    setIsLoading(false);
  };

  const attachIssue = async (issueId) => {
    setIsLoading(true);
    try {
      await axios.post(
        apiUrl + `/api/jira/${entityName}/issue/attach/${selectedTicket._id}`,
        {
          issueId,
          ticketUrl: _.get(window, "location.href"),
          reporter: _.get(userData, "team.name", ""),
        }
      );
      setIsNewIssueAdded(true);
      setOpenAttachOldIssue(false);
      setSearchIssueText("");
      setSearchResults([]);
      setSelectedIssue({});
    } catch (err) {
      console.log(err);
      dispatchSnackbar({
        message: formatMessage({ id: "global.snackbar.error.attachingIssue" }),
        variant: "error",
      });
    }
    setIsLoading(false);
  };

  const renderCreateIssueForm = () => {
    return (
      <>
        <StyledList style={{ background: "white" }}>
          <div className="formField">
            <div className="inputTitle startRow" style={{ color: "#555" }}>
              {formatMessage({ id: "jiraIssue.selection.labelName.project" })}
              <span className="required" style={{ marginRight: 2 }}>
                *
              </span>
            </div>
            <AutoCompleteDropdown
              fullWidth={true}
              size={SIZE.LARGE}
              placeholder={formatMessage({
                id: "jiraIssue.selection.placeholder.project",
              })}
              options={_.map(_.get(formData, "projectIssues", []), (o) => {
                return { label: o.name, value: o.id };
              })}
              value={project}
              control={control}
              name="project"
              onChange={(e) => {
                setProject(e.target.value);
                setIssueType(null);
                clearErrors();
              }}
              errors={_.get(errorsInfo, "name.message")}
              rules={{
                required: {
                  value: true,
                  message:
                    formatMessage({ id: "global.commonWord.name" }) +
                    formatMessage({ id: "global.error.required" }),
                },
              }}
            />
          </div>
          <div className="formField">
            <div className="inputTitle" style={{ color: "#555" }}>
              {formatMessage({ id: "jiraIssue.selection.labelName.type" })}
              <span className="required" style={{ marginRight: 2 }}>
                *
              </span>
            </div>
            <AutoCompleteDropdown
              fullWidth={true}
              size={SIZE.LARGE}
              placeholder={formatMessage({
                id: "jiraIssue.selection.placeholder.type",
              })}
              options={issueTypeOptions ?? []}
              value={issueType}
              control={control}
              name="issueType"
              onChange={(e) => {
                setIssueType(e.target.value);
              }}
              errors={_.get(errorsInfo, "issueType.message")}
              rules={{
                required: {
                  value: true,
                  message:
                    "Issu type " +
                    formatMessage({ id: "global.error.required" }),
                },
              }}
            />
          </div>
          <div className="formField">
            <div className="inputTitle" style={{ color: "#555" }}>
              {formatMessage({ id: "jiraIssue.selection.labelName.assignee" })}
            </div>
            <AutoCompleteDropdown
              fullWidth={true}
              size={SIZE.LARGE}
              placeholder={formatMessage({
                id: "jiraIssue.selection.placeholder.assignee",
              })}
              options={assigneeOptions ?? []}
              value={assignee}
              onChange={(e) => {
                setAssignee(e.target.value);
              }}
            />
          </div>
          <div className="formField">
            <InputField
              size={SIZE.LARGE}
              placeholder={formatMessage({
                id: "jiraIssue.selection.placeholder.summary",
              })}
              title={formatMessage({ id: "global.column.summary" })}
              control={control}
              name="summary"
              isRequired={true}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
              errors={_.get(errorsInfo, "summary.message")}
              rules={{
                required: {
                  value: true,
                  message:
                    `${formatMessage({ id: "global.column.summary" })}` +
                    " " +
                    formatMessage({ id: "global.error.required" }),
                },
              }}
            />
          </div>
          <div className="inputTitle" style={{ color: "#555" }}>
            {formatMessage({ id: "global.column.description" })}
            <span className="required" style={{ marginRight: 2 }}>
              *
            </span>
          </div>
          <div className="formField">
            <div
              style={{
                border: "1px solid #E6E6E6",
                borderRadius: 3,
                padding: 8,
              }}
            >
              <InputField
                control={control}
                name="description"
                minRows={5}
                placeholder={formatMessage({
                  id: "jiraIssue.selection.placeholder.description",
                })}
                multiline={true}
                value={description}
                isRequired={true}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: _.get(errorsInfo, "description.message")
                      ? "1px solid #e0e0e0"
                      : "none",
                  },
                }}
                errors={_.get(errorsInfo, "description.message")}
                rules={{
                  required: {
                    value: true,
                    message:
                      formatMessage({ id: "global.column.description" }) +
                      formatMessage({ id: "global.error.required" }),
                  },
                }}
              />
              <StyledIssueSignature>
                <p>{initDescription}</p>
              </StyledIssueSignature>
            </div>
          </div>
          <div className="formField">
            <div className="inputTitle">
              {formatMessage({ id: "global.commonWord.image" })} （ jpg, png,
              pdf ）
            </div>
            <div style={{ position: "relative" }}>
              <div>
                <ImageUpload
                  acceptType={["png", "jpg", "heic", "jpeg", "pdf"]}
                  uploadComponent={
                    <StyledUploadBoxArea className="column">
                      <i
                        className="meta-crm-icon-ic_uploadImage"
                        style={{
                          color: theme.customColors.grey[300],
                          fontSize: 60,
                        }}
                      />
                      <TinyText
                        style={{
                          color: theme.customColors.grey[500],
                          paddingTop: 4,
                        }}
                      >
                        {formatMessage({
                          id: "jiraIssue.imageUpload.placeholder",
                        })}
                      </TinyText>
                    </StyledUploadBoxArea>
                  }
                  onChange={(list) => {
                    addImages(list);
                  }}
                />
                <StyledUploadAllImgText>
                  <i className="meta-crm-icon-ic_info font-size-12" />
                  <p
                    style={{
                      cursor: "pointer",
                      textDecorationLine: "underline",
                    }}
                    onClick={getAllRelatedFiles}
                  >
                    {formatMessage({
                      id: "jiraIssue.imageUpload.explanation.firstPart",
                    })}
                  </p>
                  <p style={{ color: theme.customColors.grey[600] }}>
                    {formatMessage({
                      id: "jiraIssue.imageUpload.explanation.secondPart",
                    })}
                  </p>
                </StyledUploadAllImgText>
              </div>

              {imgs.length ? (
                <div
                  style={{
                    borderRadius: 3,
                    padding: "8px",
                    border: "1px dashed #E6E6E6",
                  }}
                >
                  <ImagePreview
                    imgs={imgs}
                    setImgs={setImgs}
                    size={75}
                    style={{ margin: "5px" }}
                  />
                </div>
              ) : (
                false
              )}
            </div>
          </div>
        </StyledList>
        <StyledBottomBar>
          <div className="endRow">
            <Button
              color={COLOR.SECONDARY}
              variant={VARIANT.TEXT}
              onClick={() => {
                setOpenCreate(false);
                clearFormFields();
              }}
            >
              {formatMessage({ id: "global.btn.cancel" })}
            </Button>
            <Button
              disabled={!isValid}
              color={COLOR.SECONDARY}
              onClick={handleCreateIssue}
            >
              {formatMessage({ id: "global.btn.create" })}
            </Button>
          </div>
        </StyledBottomBar>
      </>
    );
  };

  const renderIssueHomeScreen = () => {
    return (
      <StyledContainer>
        <Loading open={isLoading} fullScreen={false} isChart={true} />
        <StyledContent
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            padding: "0px 18px 12px",
          }}
        >
          <div className="modalHeader" style={{ fontSize: 18, paddingTop: 41 }}>
            {"Jira"}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            {canEditTicket && (
              <Button
                style={{ width: 184 }}
                variant={VARIANT.OUTLINED}
                color={COLOR.SECONDARY}
                size={SIZE.LARGE}
                onClick={() => setOpenAttachOldIssue(true)}
              >
                {formatMessage({ id: "jiraIssue.btn.attachToOld" })}
              </Button>
            )}
            {canEditTicket && (
              <Button
                style={{ width: 184 }}
                variant={VARIANT.OUTLINED}
                color={COLOR.SECONDARY}
                size={SIZE.LARGE}
                onClick={() => setOpenCreate(true)}
              >
                {formatMessage({ id: "jiraIssue.btn.create" })}
              </Button>
            )}
          </div>
        </StyledContent>
        <StyledList style={{ paddingTop: 20 }}>
          <div>
            <div
              className="startRow"
              style={{
                color: theme.customColors.grey[700],
                fontSize: 16,
                marginBottom: 12,
              }}
            >
              {formatMessage({ id: "jiraIssue.title.linkedIssues" })}
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: theme.customColors.purple[600],
                  color: "#fff",
                  fontSize: 10,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginLeft: 4,
                }}
              >
                {issues.length}
              </span>
            </div>
            {_.map(issues, (issue, i) => {
              return (
                <IssueDetailCard
                  type="jira"
                  issue={issue}
                  position={i + 1}
                  detachIssue={detachIssue}
                  isShowDetachOption={canEditTicket}
                />
              );
            })}
          </div>
        </StyledList>
      </StyledContainer>
    );
  };

  const renderCreateIssueScreen = () => {
    return (
      <StyledContainer>
        <Loading open={isLoading} fullScreen={false} />
        <StyledContent
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          <StyledBackBtn
            onClick={() => {
              setOpenCreate(false);
              clearFormFields();
            }}
          >
            {formatMessage({ id: "global.btn.back" })}
            <i
              className="meta-crm-icon-ic_redo font-size-14"
              style={{ marginLeft: 4 }}
            />
          </StyledBackBtn>
          <div className="modalHeader" style={{ fontSize: 18 }}>
            {formatMessage({ id: "jiraIssue.title.create" })}
          </div>
        </StyledContent>
        {renderCreateIssueForm()}
      </StyledContainer>
    );
  };

  const renderAttachOldIssueScreen = () => {
    return (
      <StyledContainer>
        <Loading open={isLoading} fullScreen={false} isChart={true} />
        <StyledContent
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          <StyledBackBtn
            onClick={() => {
              setOpenAttachOldIssue(false);
              setSearchResults([]);
              setSearchIssueText("");
              setSelectedIssue({});
            }}
          >
            {formatMessage({ id: "global.btn.back" })}
            <i
              className="meta-crm-icon-ic_redo font-size-14"
              style={{ marginLeft: 4 }}
            />
          </StyledBackBtn>
          <div className="modalHeader" style={{ fontSize: 18 }}>
            {formatMessage({ id: "jiraIssue.title.attach" })}
          </div>
          <div style={{ position: "relative" }}>
            <InputField
              size={SIZE.MEDIUM}
              placeholder={formatMessage({
                id: "jiraIssue.searchBar.placeholder",
              })}
              onEnter={() => debouncedSearch(searchIssueText)}
              onChange={handleSearch}
              value={searchIssueText}
              type="search"
              onClean={() => {
                setSearchIssueText("");
                setShowSearchResult(false);
              }}
            />
            {isSearchResultLoading && (
              <StyledIssueDropdown style={{ minHeight: 200 }}>
                <Loading
                  open={true}
                  fullScreen={false}
                  text={
                    <Box sx={{ fontSize: 12, fontWeight: 400 }}>
                      {"Searching issues..."}
                    </Box>
                  }
                />
              </StyledIssueDropdown>
            )}
            {showSearchResult && (
              <StyledIssueDropdown ref={dropdownRef}>
                {searchResults.length ? (
                  <>
                    {_.map(searchResults, (issue, i) => {
                      return (
                        <StyledIssueDropdownItem
                          key={i}
                          onClick={() => {
                            setSelectedIssue(issue);
                            setShowSearchResult(false);
                          }}
                        >
                          {issue.title.length > 50
                            ? issue.title.slice(0, 50) + "..."
                            : issue.title}
                        </StyledIssueDropdownItem>
                      );
                    })}
                  </>
                ) : (
                  <StyledNoResultFound>
                    {formatMessage({ id: "global.error.noResultFound" })}
                  </StyledNoResultFound>
                )}
              </StyledIssueDropdown>
            )}
          </div>
        </StyledContent>
        <StyledList>
          {!_.isEmpty(selectedIssue) && (
            <>
              <div
                style={{
                  color: theme.customColors.grey[700],
                  fontSize: 16,
                  margin: "6px 0px",
                }}
              >
                {formatMessage({ id: "jiraIssue.title.searchResult" })}
              </div>
              <IssueDetailCard
                type="jira"
                canEditTicket
                issue={selectedIssue}
                position={issues.length + 1}
                isShowDetachOption={false}
                detachIssue={() => {}}
              />
            </>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              disabled={_.isEmpty(selectedIssue)}
              color={COLOR.SECONDARY}
              size={SIZE.SMALL}
              onClick={() => attachIssue(selectedIssue.issueId)}
            >
              {formatMessage({ id: "jiraIssue.btn.attachToThis" })}
            </Button>
            <Button
              color={COLOR.SECONDARY}
              size={SIZE.SMALL}
              variant={VARIANT.OUTLINED}
              onClick={() => {
                setOpenAttachOldIssue(false);
                setSearchResults([]);
                setSearchIssueText("");
                setSelectedIssue({});
              }}
            >
              {formatMessage({ id: "global.btn.cancel" })}
            </Button>
          </div>
        </StyledList>
      </StyledContainer>
    );
  };

  if (!isJiraConnected) {
    return (
      <IssueIntegrationView
        canEdit={canEditIntegration}
        title={formatMessage({ id: "emptyStatus.notIntegrated.title" })}
        message={formatMessage({
          id: "emptyStatus.notIntegrated.description.jiraIssue",
        })}
      />
    );
  }

  if (openCreate) return renderCreateIssueScreen();

  if (openAttachOldIssue) return renderAttachOldIssueScreen();

  return renderIssueHomeScreen();
};

export default JiraIssue;
